/**
 * Guest Portal ▸ Pages (owner, 2026-10-26: "a proper lightweight CMS"): the
 * blocks a guest page is made of, and the page's wire shape. A block is DATA
 * that one fixed component renders (GuestBlocks.tsx); nothing typed by staff
 * is ever HTML. Mirrors api/Modules/GuestPortal/GuestPageContent.cs, which
 * validates every block on save and again on read and is the authority.
 */
import type { PublicNav, PublicSite } from './publicGuides.api';

export type GuestBlockType =
  | 'heading'
  | 'text'
  | 'image'
  | 'gallery'
  | 'callout'
  | 'button'
  | 'faq'
  | 'contact'
  | 'list'
  | 'divider';

interface Base<T extends GuestBlockType> {
  id: string;
  type: T;
}

export interface HeadingBlock extends Base<'heading'> {
  text: string;
  level: 2 | 3;
}
export interface TextBlock extends Base<'text'> {
  /** The guide pages' mini markdown: **bold**, *italic*, [text](url), line breaks. */
  text: string;
}
export interface ImageBlock extends Base<'image'> {
  url: string;
  alt?: string;
  caption?: string;
}
export interface GalleryBlock extends Base<'gallery'> {
  images: { url: string; caption?: string }[];
}
export interface CalloutBlock extends Base<'callout'> {
  tone: 'info' | 'warning';
  title?: string;
  text: string;
}
export interface ButtonBlock extends Base<'button'> {
  label: string;
  href: string;
  style: 'primary' | 'secondary';
}
export interface FaqBlock extends Base<'faq'> {
  title?: string;
  items: { q: string; a: string }[];
}
export interface ContactBlock extends Base<'contact'> {
  title?: string;
  text?: string;
}
export type ListSource = 'places' | 'tours' | 'pickup';
export interface ListItem {
  name: string;
  meta?: string;
  description?: string;
  image?: string;
  href?: string;
}
export interface ListBlock extends Base<'list'> {
  source: ListSource;
  /** A city's slug (places, tours). */
  city?: string;
  /** A pick-up region's code (pickup); none = every region. */
  region?: string;
  limit: number;
  title?: string;
  /** Filled by the API from the guest content. */
  items?: ListItem[];
  place?: string | null;
  moreHref?: string | null;
}
export type DividerBlock = Base<'divider'>;

export type GuestBlock =
  | HeadingBlock
  | TextBlock
  | ImageBlock
  | GalleryBlock
  | CalloutBlock
  | ButtonBlock
  | FaqBlock
  | ContactBlock
  | ListBlock
  | DividerBlock;

export interface CustomPageBody {
  slug: string;
  title: string;
  summary: string | null;
  heroUrl: string | null;
  blocks: GuestBlock[];
}

/** GET /api/public/pages/p/<slug> (and the editor's preview). */
export interface CustomPageView {
  site: PublicSite;
  nav: PublicNav;
  page: CustomPageBody;
}

/** The messages between the page editor and its preview frames (/p/_preview, same origin only). */
export const PREVIEW_READY = 'amassa:guest-preview:ready';
export const PREVIEW_VIEW = 'amassa:guest-preview:view';

/** The blocks, in the order the editor's palette offers them, with what each is for. */
export const BLOCK_KINDS: readonly { type: GuestBlockType; label: string; hint: string }[] = [
  { type: 'heading', label: 'Heading', hint: 'A title for a part of the page' },
  { type: 'text', label: 'Text', hint: 'Paragraphs, with bold, italic and links' },
  { type: 'image', label: 'Image', hint: 'One photo with a caption' },
  { type: 'gallery', label: 'Gallery', hint: 'A grid of photos' },
  { type: 'callout', label: 'Callout', hint: 'A highlighted note or a warning' },
  { type: 'button', label: 'Button', hint: 'A link to a guest page, a website, a phone or an email' },
  { type: 'faq', label: 'Questions', hint: 'Questions guests open one by one' },
  { type: 'contact', label: 'Contact card', hint: 'Your hotlines and email, always current' },
  { type: 'list', label: 'From your content', hint: 'Recommendations, tours or pick-up points of a city' },
  { type: 'divider', label: 'Divider', hint: 'A quiet line between parts' },
];

export const LIST_SOURCES: readonly { id: ListSource; label: string }[] = [
  { id: 'places', label: 'Local Recommendations' },
  { id: 'tours', label: 'Optional Tours' },
  { id: 'pickup', label: 'Pick-up points' },
];

/** A short random block id ([a-z0-9], as the API accepts). */
export function newBlockId(): string {
  return Math.random().toString(36).slice(2, 12).replace(/[^a-z0-9]/g, '') || 'b' + Date.now().toString(36);
}

/** A fresh block of a kind, ready to fill in. */
export function newBlock(type: GuestBlockType): GuestBlock {
  const id = newBlockId();
  switch (type) {
    case 'heading':
      return { id, type, text: '', level: 2 };
    case 'text':
      return { id, type, text: '' };
    case 'image':
      return { id, type, url: '' };
    case 'gallery':
      return { id, type, images: [] };
    case 'callout':
      return { id, type, tone: 'info', text: '' };
    case 'button':
      return { id, type, label: '', href: '', style: 'primary' };
    case 'faq':
      return { id, type, items: [{ q: '', a: '' }] };
    case 'contact':
      return { id, type };
    case 'list':
      return { id, type, source: 'places', limit: 6 };
    case 'divider':
      return { id, type };
  }
}

/** The address a guest page lives at. */
export function guestPagePath(slug: string): string {
  return `/p/${slug}`;
}

/** A slug from a title, as the API derives it: ASCII, lower case, words joined by dashes. */
export function slugFrom(title: string): string {
  return title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '');
}

export const SLUG_RX = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** The links a button or markdown may hold: https, mailto:, tel:, or a guest path on this host. */
export function isGuestLink(href: string): boolean {
  const h = href.trim();
  if (h.startsWith('/')) return /^\/(guide|places|tours|gifts|gift-list|pickup|p)(\/[a-z0-9-]{1,64})?\/?$/.test(h);
  if (/^mailto:/i.test(h)) return /^mailto:[^@\s<>"]{1,64}@[^@\s<>"]{1,190}$/i.test(h);
  if (/^tel:/i.test(h)) return /^tel:\+?[0-9 ()-]{3,24}$/i.test(h);
  return /^https:\/\/[^\s/]+/i.test(h);
}

/** The images a page may show: https, or this host's uploads. */
export function isGuestImage(url: string): boolean {
  const u = url.trim();
  if (u.startsWith('/')) return /^\/uploads\/[A-Za-z0-9/_\-.]{1,400}$/.test(u) && !u.includes('..');
  return /^https:\/\/[^\s/]+\S*$/i.test(u);
}
